import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { INTL_DMY_CASUAL_FORMAT, INTL_LONG_WEEKDAY } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'

// LuxKit Components
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import ReturnFareFreeLabel from 'components/Common/Labels/ReturnFareFreeLabel'
import Heading from 'LuxKit/Typography/Heading'
import MaldivesWasPriceRow from './MaldivesWasPriceRow'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import SolidExclamationCircleIcon from 'LuxKit/Icons/solid/SolidExclamationCircleIcon'
import { FlightsClassTypes, REWARD_FOR_BASE_CABIN, type FareClassWithReward } from 'constants/flight'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
// Hooks
import { useCalendarItemData } from '../hooks/useCalendarItemData'
import { useCharterRewardAvailabilityContext } from '../hooks/charterRewardAvailabilityContext'
import type { CalendarItemBundleData } from '../hooks/types'
import type { MaldivesCabinHotelEntry } from '../hooks/useMaldivesOffersPricing'
import useCalendarItemPricing from './useCalendarItemPricing'
import { computeFareClassCellPrice } from './maldivesCharterTripOptionsTablePricing'
import { getCharterSelectableCashCabins } from 'lib/maldivesCharter/charterCabins'
import { useAppSelector } from 'hooks/reduxHooks'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import { REWARD_PREVIEW_RATE_BY_CABIN } from 'lib/maldivesCharter/classicRewardPricing'
import { shouldShowReturnFareFreeCalendarLabel } from './returnFareFreeCalendarVisibility'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { useSeatEligiblePassengers } from '../hooks/utils'
import MaldivesFnfSpecialOffer from './MaldivesFnfSpecialOffer'
import MaldivesCharterRewardSeatsAvailableLabel from './MaldivesCharterRewardSeatsAvailableLabel'
import MaldivesCharterDateFareCarousel from './MaldivesCharterDateFareCarousel'
import MaldivesCharterDateRowFareToggle from './MaldivesCharterDateRowFareToggle'
import type { MaldivesCharterTripOption } from './MaldivesCharterFareCarouselOption'
import { getMaldivesMobileCalendarPriceContextLabels } from './maldivesMobileCalendarPriceContext'
import { oneWayFlightMessages } from './MaldivesCharterOneWayFlightSaleLabel'

// Utils
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import KidsFlyFreeLabel from 'components/Common/Labels/KidsFlyFreeLabel'
import useKidsFlyFree from 'hooks/CharterFlights/useKidsFlyFree'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'

// Types
interface TripOption {
  id: string
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  price: number
  memberPrice?: number
  currency: string
  seatsLeft?: number
  nights: number
  available: boolean
  fareClass?: FareClassWithReward
}

interface Props {
  oneWayBundle?: boolean
  missingFlightLeg?: 'depart' | 'return'
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  nights: number
  rooms: Array<App.Occupants>
  fareClass: FlightsClassTypes
  selectedFareClass?: FareClassWithReward | null
  priceMode: 'total' | 'perPerson'
  luxPlusEnabled: boolean
  isSelected: boolean
  onSelect: (option: TripOption) => void
  prefetchedBundleData?: CalendarItemBundleData
  /** Available fare classes (including reward variants) for the per-date fare carousel. */
  allowedFareClasses?: ReadonlyArray<FareClassWithReward>
  /** Callback when the user picks a different fare class from the carousel. */
  onFareClassChange?: (fareClass: FareClassWithReward) => void
  feederOriginAirportCode?: string
  /** When true, show reward pricing on the collapsed date card. */
  rewardSeatsOnly?: boolean
  /** Cabin filter from the dropdown — determines which cabin's price to show on the collapsed card. */
  cabinFilter?: FlightsClassTypes | null
  hotelPriceByCabinAndDate?: Partial<Record<FlightsClassTypes, Record<string, MaldivesCabinHotelEntry>>>
}

// Styled Components - Mobile specific layout
const DateCard = styled.div<{ $clickable?: boolean }>`
  padding: ${rem(12)} ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  ${p => p.$clickable && 'cursor: pointer;'}
`

const SelectButton = styled(TextButton)`
  min-width: ${rem(80)};
`

const DateRangeContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: center;

  &.missing-return {
    grid-template-columns: 1fr 0.5fr 1.5fr;
  }

  &.missing-depart {
    grid-template-columns: 1.5fr 0.5fr 1fr;
  }
`

const FlightDateHeading = styled(Heading)`
  &.missing-flight {
    max-inline-size: ${rem(170)};
    white-space: normal;
  }
`

const SmallSup = styled.sup`
  font-size: 0.6em;
  vertical-align: super;
`

function MaldivesCharterDateFareCalendarItemMobile(props: Props) {
  const {
    departDate,
    returnDate,
    checkIn,
    checkOut,
    oneWayBundle,
    missingFlightLeg,
    nights,
    rooms,
    fareClass,
    selectedFareClass,
    priceMode,
    luxPlusEnabled,
    isSelected,
    onSelect,
    prefetchedBundleData,
    allowedFareClasses,
    onFareClassChange,
    feederOriginAirportCode,
    rewardSeatsOnly = false,
    cabinFilter,
    hotelPriceByCabinAndDate,
  } = props

  const intl = useIntl()
  const isInfiniteMember = useAppSelector(state => getLoyaltyIsInfiniteAccountTier(state))
  const [fareCarouselExpanded, setFareCarouselExpanded] = useState(false)
  const seatEligiblePassengers = useSeatEligiblePassengers(rooms)
  const totalPassengers = useMemo(() => sumUpOccupancies(rooms), [rooms])
  const isLuxRowEnabled = useFeatureFlag(OptimizelyFeatureFlags.luxRow)
  const selectableCashCabins = useMemo(
    () => getCharterSelectableCashCabins(isLuxRowEnabled),
    [isLuxRowEnabled],
  )
  const adultCount = useMemo(() => rooms.reduce((sum, room) => sum + (room.adults ?? 0), 0), [rooms])
  const { isKidsFlyFreeDate } = useKidsFlyFree({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    occupancies: rooms,
    fareClass,
  })

  const fetchedBundleData = useCalendarItemData({
    checkIn,
    checkOut,
    nights,
    rooms,
    fareClass,
    priceMode,
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    feederOriginAirportCode,
    disabled: !!prefetchedBundleData,
  })

  const bundleData = prefetchedBundleData ?? fetchedBundleData

  const { isAvailableForDate: isRewardAvailableForDate, getLegResult, getJourneysForCabinDate } = useCharterRewardAvailabilityContext()
  const showRewardSeatsLabel = isRewardAvailableForDate(checkIn)

  // Unified collapsed-card price — use the same per-cabin sources as the desktop trip options
  // table whenever they are available. The aggregate bundle can be unavailable when its
  // cheapest hotel allocation does not match a cabin, even though another cabin has a valid
  // hotel + flight price for the same dates.
  const { bestCashPrice, bestRewardPrice } = useMemo(() => {
    const cabinsToCheck = cabinFilter ? [cabinFilter] : selectableCashCabins
    let bestCash: { cashTotal: number; cashMemberTotal?: number } | undefined
    let bestReward: { pointsTotal: number; cashTotal: number; cashMemberTotal?: number } | undefined

    // Cash: scan all visible cabins when no cabin filter is selected, matching the desktop
    // table's per-cabin fallback instead of relying only on the aggregate bundle price.
    for (const cabin of cabinsToCheck) {
      const journeys = getJourneysForCabinDate(cabin, checkIn)
      if (!journeys?.departing && !journeys?.returning) continue

      const cabinHotel = hotelPriceByCabinAndDate?.[cabin]?.[checkIn]
      const hotelPrice = cabinHotel
        ? (luxPlusEnabled ? (cabinHotel.hotelMemberPrice || cabinHotel.hotelPrice || 0) : (cabinHotel.hotelPrice || 0))
        : (bundleData.hotelPrice ?? (bundleData.flightPrice != null ? Math.max(0, bundleData.totalPrice - bundleData.flightPrice) : undefined))
      const hotelMemberPrice = cabinHotel?.hotelMemberPrice ?? bundleData.hotelMemberPrice
      const price = computeFareClassCellPrice({
        fareClass: cabin,
        hotelPrice,
        hotelMemberPrice,
        departingFlight: journeys.departing,
        returningFlight: journeys.returning,
        seatEligiblePassengers,
        getLegResult,
      })
      if (price.cashTotal == null) continue

      const displayedPrice = luxPlusEnabled && price.cashMemberTotal != null
        ? price.cashMemberTotal
        : price.cashTotal
      const bestDisplayedPrice = bestCash && (luxPlusEnabled && bestCash.cashMemberTotal != null
        ? bestCash.cashMemberTotal
        : bestCash.cashTotal)
      if (!bestCash || bestDisplayedPrice == null || displayedPrice < bestDisplayedPrice) {
        bestCash = { cashTotal: price.cashTotal, cashMemberTotal: price.cashMemberTotal }
      }
    }

    // Reward: scan visible cabins inline (same as desktop), using hotelPriceByCabinAndDate first
    for (const cabin of cabinsToCheck) {
      const journeys = getJourneysForCabinDate(cabin, checkIn)
      if (!journeys || (!oneWayBundle && (!journeys.departing || !journeys.returning)) ||
        (oneWayBundle && !journeys.departing && !journeys.returning)) continue

      const cabinHotel = hotelPriceByCabinAndDate?.[cabin]?.[checkIn]
      let hotelForReward = 0
      if (cabinHotel) {
        hotelForReward = luxPlusEnabled
          ? (cabinHotel.hotelMemberPrice || cabinHotel.hotelPrice || 0)
          : (cabinHotel.hotelPrice || 0)
      } else if (bundleData.available) {
        const cabinFlightFare = (journeys.departing?.price?.all?.totalFare ?? 0)
          + (journeys.returning?.price?.all?.totalFare ?? 0)
          + (journeys.departing?.feederEstimatedPrice ?? 0)
          + (journeys.returning?.feederEstimatedPrice ?? 0)
        const bundleTotal = luxPlusEnabled ? (bundleData.memberTotalPrice || bundleData.totalPrice || 0) : (bundleData.totalPrice || 0)
        hotelForReward = Math.max(0, bundleTotal - cabinFlightFare)
      }
      if (!hotelForReward) continue

      const rewardFareClass = REWARD_FOR_BASE_CABIN[cabin]
      if (!rewardFareClass) continue
      const rewardPrice = computeFareClassCellPrice({
        fareClass: rewardFareClass,
        hotelPrice: hotelForReward,
        departingFlight: journeys.departing,
        returningFlight: journeys.returning,
        allowIncompleteJourneys: !!oneWayBundle,
        seatEligiblePassengers,
        getLegResult,
      })
      if (rewardPrice.pointsTotal == null || rewardPrice.cashTotal == null) continue
      const rawPoints = rewardPrice.pointsTotal
      const rawCash = rewardPrice.cashTotal
      const totalPoints = priceMode === 'perPerson' && totalPassengers > 0 ? rawPoints / totalPassengers : rawPoints
      const totalCash = priceMode === 'perPerson' && totalPassengers > 0 ? rawCash / totalPassengers : rawCash

      if (!bestReward || totalCash < bestReward.cashTotal) {
        bestReward = { pointsTotal: totalPoints, cashTotal: totalCash }
      }
    }

    return { bestCashPrice: bestCash, bestRewardPrice: bestReward }
  }, [cabinFilter, selectableCashCabins, getJourneysForCabinDate, checkIn, hotelPriceByCabinAndDate, seatEligiblePassengers, getLegResult, luxPlusEnabled, bundleData, priceMode, totalPassengers, oneWayBundle])

  // Preview pricing for logged-out / non-member users: hardcoded rate + hotel from bundleData
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const rewardLockedForLogin = rewardSeatsOnly && (!memberId || !hasLoyaltyAccount)
  const rewardPreviewPrice = useMemo(() => {
    // Only show preview when the cabin actually has availability for this date
    const cabinAvailable = !!bestCashPrice || bundleData.available
    if (!rewardLockedForLogin || !cabinAvailable) return undefined
    const cabinsToCheck = cabinFilter ? [cabinFilter] : selectableCashCabins
    let cheapestRate: { pointsPerPerson: number; cashPerPerson: number } | undefined
    for (const cabin of cabinsToCheck) {
      const rate = REWARD_PREVIEW_RATE_BY_CABIN[cabin]
      if (rate && (!cheapestRate || rate.cashPerPerson < cheapestRate.cashPerPerson)) {
        cheapestRate = rate
      }
    }
    if (!cheapestRate) return undefined
    // Hotel from hotelPriceByCabinAndDate or bundleData
    let hotelPrice = 0
    for (const cabin of cabinsToCheck) {
      const h = hotelPriceByCabinAndDate?.[cabin]?.[checkIn]
      const hp = h ? (luxPlusEnabled ? (h.hotelMemberPrice || h.hotelPrice || 0) : (h.hotelPrice || 0)) : 0
      if (hp > 0 && (!hotelPrice || hp < hotelPrice)) hotelPrice = hp
    }
    if (!hotelPrice && bundleData.hotelPrice) hotelPrice = bundleData.hotelPrice
    if (!hotelPrice) return undefined
    const rawPoints = cheapestRate.pointsPerPerson * seatEligiblePassengers
    const rawCash = hotelPrice + cheapestRate.cashPerPerson * seatEligiblePassengers
    return {
      pointsTotal: priceMode === 'perPerson' && totalPassengers > 0 ? rawPoints / totalPassengers : rawPoints,
      cashTotal: priceMode === 'perPerson' && totalPassengers > 0 ? rawCash / totalPassengers : rawCash,
    }
  }, [rewardLockedForLogin, bundleData.available, bundleData.hotelPrice, cabinFilter, selectableCashCabins, bestCashPrice, hotelPriceByCabinAndDate, checkIn, luxPlusEnabled, seatEligiblePassengers, priceMode, totalPassengers])

  // Override bundleData with the per-cabin cash price so useCalendarItemPricing picks it up
  const effectiveBundleData = useMemo(() => {
    // When a cabin-specific price exists, use it even if the aggregate bundle was unavailable.
    // The aggregate can fail because its cheapest hotel allocation is not valid for that cabin.
    if (!bestCashPrice && cabinFilter) {
      return { ...bundleData, available: false, totalPrice: 0, pricePerPerson: 0, memberTotalPrice: undefined, memberPricePerPerson: undefined }
    }
    if (!bestCashPrice) return bundleData
    const totalPassengers = Math.max(1, seatEligiblePassengers)
    return {
      ...bundleData,
      available: bundleData.available || !!bestCashPrice,
      totalPrice: bestCashPrice.cashTotal,
      memberTotalPrice: bestCashPrice.cashMemberTotal,
      pricePerPerson: bestCashPrice.cashTotal / totalPassengers,
      memberPricePerPerson: bestCashPrice.cashMemberTotal ? bestCashPrice.cashMemberTotal / totalPassengers : undefined,
    }
  }, [bestCashPrice, cabinFilter, bundleData, seatEligiblePassengers])

  const {
    regularPrice,
    memberPrice,
    isMemberPrice,
    displayPrice,
    wasPrice,
    shouldShowWasPrice,
    inventoryMessageType,
    inventoryMessage,
    fnfInventoryMessage,
  } = useCalendarItemPricing({
    bundleData: effectiveBundleData,
    luxPlusEnabled,
    priceMode,
  })
  const hasDisplayPrice = displayPrice !== undefined && displayPrice > 0
  const showReturnFareFree = luxPlusEnabled && shouldShowReturnFareFreeCalendarLabel({
    hasReturnFareFree: bundleData.hasReturnFareFree,
    isEconomy: !cabinFilter || cabinFilter === FlightsClassTypes.ECONOMY,
  })
  const showInventoryMessage = inventoryMessage !== '' && (!bundleData.isFnfPriceApplied || inventoryMessageType !== 'seat')

  const option: TripOption = {
    id: `${checkIn}-${nights}n`,
    departDate: departDate,
    returnDate: returnDate,
    checkIn: checkIn,
    checkOut: checkOut,
    nights,
    price: regularPrice,
    memberPrice: memberPrice,
    currency: 'AUD',
    seatsLeft: bundleData.seatsLeft,
    available: effectiveBundleData.available,
    fareClass: selectedFareClass ?? undefined,
  }

  const selectedText = intl.formatMessage({
    id: 'acuxSK1zMav7-Sn9mh4K',
    defaultMessage: 'Selected',
    description: 'Button label when a charter flight date is currently selected',
  })
  const unavailableText = intl.formatMessage({
    id: 'lbS33CL-vQm3bmd7lueV',
    defaultMessage: 'Sold out!',
    description: 'Short label when a fare column has no reward-seat inventory or no seats for that cabin',
  })
  const loadingText = intl.formatMessage({
    id: 'C6m8UbIIgveZbgrQtJ6T',
    defaultMessage: 'Loading...',
    description: 'Loading state text while charter flight prices are being fetched',
  })
  const { nightsText, priceContextText, expandedPriceContextText } = getMaldivesMobileCalendarPriceContextLabels(
    intl,
    nights,
    priceMode,
    adultCount,
  )
  const dayOffsetText = intl.formatNumber(1, { signDisplay: 'always' })
  const changeFareLabel = intl.formatMessage({
    id: 'Rt6uQBd5FYfbS4hAV4ab',
    defaultMessage: 'Change fare',
    description: 'Link to reopen the fare carousel for an already-selected Maldives charter date',
  })
  const expandAriaLabel = intl.formatMessage({
    id: '711wxG49umcK97pt8pS-',
    defaultMessage: 'Show fare options for these dates',
    description: 'Accessibility label for expanding the fare carousel beneath the row.',
  })
  const collapseAriaLabel = intl.formatMessage({
    id: '32x_r3aTH5F8f-0HHeLj',
    defaultMessage: 'Hide fare options for these dates',
    description: 'Accessibility label for collapsing the fare carousel shown below the row.',
  })
  const unavailableAriaLabel = intl.formatMessage({
    id: 'OwJEwq2JWiXn3Mtk7a4k',
    defaultMessage: 'Fare options unavailable for these dates',
    description: 'Accessibility label for the disabled fare-toggle control.',
  })

  // Render price display
  let priceDisplay
  if (bundleData.fetching) {
    priceDisplay = (
      <BodyText variant="small" colour="light-foreground-alt">
        {loadingText}
      </BodyText>
    )
  } else if (rewardSeatsOnly && bestRewardPrice) {
    priceDisplay = (
      <>
        <Heading as="div" variant="heading4" colour={luxPlusEnabled ? 'lux-plus' : undefined} wrap="no-wrap">
          <FormatLoyaltyPoints abbreviated points={bestRewardPrice.pointsTotal} />
        </Heading>
        <Caption variant="medium" colour={luxPlusEnabled ? 'lux-plus' : 'neutral-two'} weight="normal">
          <FormattedMessage
            id="peHFjMVc2YKKQdYS2mU5"
            defaultMessage="+ {cash}"
            description="Cash co-pay portion of a Société reward-seat bundle in the Maldives charter calendar, shown under the points total in a smaller line with a leading plus sign before the formatted currency amount."
            values={{ cash: <FormatCurrency value={bestRewardPrice.cashTotal} format="roundedDollar" /> }}
          />
        </Caption>
      </>
    )
  } else if (rewardSeatsOnly && !bestRewardPrice && rewardPreviewPrice) {
    priceDisplay = (
      <>
        <Heading as="div" variant="heading4" colour={luxPlusEnabled ? 'lux-plus' : undefined} wrap="no-wrap">
          <FormatLoyaltyPoints abbreviated points={rewardPreviewPrice.pointsTotal} />
        </Heading>
        <Caption variant="medium" colour={luxPlusEnabled ? 'lux-plus' : 'neutral-two'} weight="normal">
          <FormattedMessage
            id="peHFjMVc2YKKQdYS2mU5"
            defaultMessage="+ {cash}"
            description="Cash co-pay portion of a Société reward-seat bundle in the Maldives charter calendar, shown under the points total in a smaller line with a leading plus sign before the formatted currency amount."
            values={{ cash: <FormatCurrency value={rewardPreviewPrice.cashTotal} format="roundedDollar" /> }}
          />
        </Caption>
      </>
    )
  } else if (rewardSeatsOnly && !bestRewardPrice) {
    priceDisplay = (
      <BodyText variant="medium" colour="neutral-three">
        {intl.formatMessage({
          id: 'lbS33CL-vQm3bmd7lueV',
          defaultMessage: 'Sold out!',
          description: 'Short label when a fare column has no reward-seat inventory or no seats for that cabin',
        })}
      </BodyText>
    )
  } else if (hasDisplayPrice) {
    const perPersonLabel = intl.formatMessage({
      id: 'ew2ARy7ro3tFSM4inpHN',
      defaultMessage: '/person',
      description: 'Per person pricing unit suffix in Maldives calendar',
    })
    const totalLabel = intl.formatMessage({
      id: 'j--PuUYLTgWUxAxROh0Z',
      defaultMessage: 'total',
      description: 'Total pricing unit suffix in the Maldives calendar',
    })
    priceDisplay = (
      <>
        <div>
          <FormattedMessage
            id="eEOhCaC58xEuJ7A6ucxl"
            defaultMessage="{price} {unit}"
            description="Price amount followed by pricing unit (per person or total) in Maldives flight calendar mobile"
            values={{
              price: <Heading as="span" variant="heading4" colour={isMemberPrice ? 'lux-plus' : undefined} wrap="no-wrap">
                <FormatCurrency value={displayPrice} format="roundedDollar" />
              </Heading>,
              unit: <Caption as="span" variant="large" colour={isMemberPrice ? 'lux-plus' : 'light-foreground-alt'} weight="normal" format="lowercase">
                {priceMode === 'perPerson' ? perPersonLabel : totalLabel}
              </Caption>,
            }}
          />
        </div>
        {shouldShowWasPrice && wasPrice != null && <MaldivesWasPriceRow
          altPrice={wasPrice}
          saleUnit={priceMode === 'perPerson' ? '/person' : undefined}
        />}
      </>
    )
  } else {
    priceDisplay = (
      <BodyText variant="medium" colour="light-foreground-alt">
        {unavailableText}
      </BodyText>
    )
  }

  useEffect(() => {
    if (isSelected) {
      setFareCarouselExpanded(true)
    }
  }, [isSelected])

  const selectControl = useMemo(() => {
    if (isSelected && !fareCarouselExpanded) {
      return (
        <Group direction="vertical" gap={4} horizontalAlign="end">
          <SelectButton
            kind="primary"
            nonInteractable
            tabIndex={-1}
          >
            {selectedText}
          </SelectButton>
          <TextLink size="caption" weight="regular" onClick={() => setFareCarouselExpanded(true)}>
            {changeFareLabel}
          </TextLink>
        </Group>
      )
    }
    if (!effectiveBundleData.available && !fareCarouselExpanded) {
      return null
    }
    return (
      <MaldivesCharterDateRowFareToggle
        expanded={fareCarouselExpanded}
        disabled={!effectiveBundleData.available}
        onToggle={() => setFareCarouselExpanded(v => !v)}
        expandAriaLabel={expandAriaLabel}
        collapseAriaLabel={collapseAriaLabel}
        unavailableAriaLabel={unavailableAriaLabel}
      />
    )
  }, [isSelected, fareCarouselExpanded, effectiveBundleData.available, selectedText, changeFareLabel, expandAriaLabel, collapseAriaLabel, unavailableAriaLabel])

  const carouselTripOption: MaldivesCharterTripOption = useMemo(() => ({
    id: option.id,
    departDate: option.departDate,
    returnDate: option.returnDate,
    checkIn: option.checkIn,
    checkOut: option.checkOut,
    nights: option.nights,
    price: option.price,
    memberPrice: option.memberPrice,
    currency: option.currency,
    seatsLeft: option.seatsLeft,
    available: option.available,
  }), [option.id, option.departDate, option.returnDate, option.checkIn, option.checkOut, option.nights, option.price, option.memberPrice, option.currency, option.seatsLeft, option.available])

  const handleCarouselPickFare = useCallback((pickedFareClass: FareClassWithReward, pickedOption: MaldivesCharterTripOption) => {
    onFareClassChange?.(pickedFareClass)
    onSelect({
      ...pickedOption,
      fareClass: pickedFareClass,
    })
  }, [onSelect, onFareClassChange])

  const shouldShowCollapsedPriceContext = bundleData.fetching || hasDisplayPrice || !!bestRewardPrice || !!rewardPreviewPrice

  return (
    <DateCard
      $clickable={effectiveBundleData.available}
      onClick={effectiveBundleData.available ? () => setFareCarouselExpanded(prev => !prev) : undefined}
    >
      <VerticalSpacer gap={12}>

        {/* Dates Row */}
        <DateRangeContainer className={missingFlightLeg ? `missing-${missingFlightLeg}` : undefined}>
          <div>
            <BodyText variant="small" colour="light-foreground-alt">
              {oneWayBundle ? intl.formatMessage(oneWayFlightMessages.depart) : intlDateFormatter(departDate, INTL_LONG_WEEKDAY, intl.locale)}
            </BodyText>
            <FlightDateHeading variant="heading5" wrap="no-wrap" className={missingFlightLeg === 'depart' ? 'missing-flight' : undefined}>
              {missingFlightLeg === 'depart'
                ? intl.formatMessage(oneWayFlightMessages.mustBeBookedSeparately)
                : intlDateFormatter(departDate, INTL_DMY_CASUAL_FORMAT, intl.locale)}
            </FlightDateHeading>
          </div>
          <Group direction="vertical" gap={0} horizontalAlign="center">
            <LineArrowRightIcon size="M" />
            <BodyText variant="small" colour="light-foreground-alt">
              {nightsText}
            </BodyText>
          </Group>
          <Group direction="vertical" horizontalAlign="end">
            <Group direction="vertical" horizontalAlign="start" gap={0}>
              <BodyText variant="small" colour="light-foreground-alt">
                {oneWayBundle
                  ? intl.formatMessage(oneWayFlightMessages.return)
                  : intlDateFormatter(checkOut, INTL_LONG_WEEKDAY, intl.locale)}
              </BodyText>
              <FlightDateHeading variant="heading5" wrap="no-wrap" className={missingFlightLeg === 'return' ? 'missing-flight' : undefined}>
                {missingFlightLeg === 'return'
                  ? intl.formatMessage(oneWayFlightMessages.mustBeBookedSeparately)
                  : intlDateFormatter(oneWayBundle ? returnDate : checkOut, INTL_DMY_CASUAL_FORMAT, intl.locale)}{!oneWayBundle && <SmallSup>{dayOffsetText}</SmallSup>}
              </FlightDateHeading>
            </Group>
          </Group>
        </DateRangeContainer>

        {/* Kids Fly Free + Availability Warning */}
        <Group direction="vertical" horizontalAlign="start" gap={4}>
          {showInventoryMessage && <Group direction="horizontal" verticalAlign="center" gap={4}>
            <SolidExclamationCircleIcon colour="urgency" size="XS" />
            <Caption variant="medium" weight="bold" colour="urgency">
              {inventoryMessage}
            </Caption>
          </Group>}
          {bundleData.isFnfPriceApplied && (
            <MaldivesFnfSpecialOffer
              fnfInventoryMessage={fnfInventoryMessage}
              isInfiniteMember={isInfiniteMember}
              isHorizontal
            />
          )}
          {isKidsFlyFreeDate(departDate) && <KidsFlyFreeLabel variant="short" />}
          {showReturnFareFree && <ReturnFareFreeLabel />}
          {showRewardSeatsLabel && <MaldivesCharterRewardSeatsAvailableLabel />}
        </Group>

        {/* Price and Select Button */}
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={12}>
          {!fareCarouselExpanded && (
            <VerticalSpacer gap={4}>
              {shouldShowCollapsedPriceContext && (
                <BodyText variant="small" colour="light-foreground-alt">
                  {priceContextText}
                </BodyText>
              )}
              {priceDisplay}
            </VerticalSpacer>
          )}
          {fareCarouselExpanded && (
            <BodyText variant="small" colour="light-foreground-alt">
              {expandedPriceContextText}
            </BodyText>
          )}
          <div onClick={e => e.stopPropagation()}>{selectControl}</div>
        </Group>
      </VerticalSpacer>
      {fareCarouselExpanded && allowedFareClasses && allowedFareClasses.length > 0 && (
        <MaldivesCharterDateFareCarousel
          fareClasses={allowedFareClasses}
          selectedFareClass={selectedFareClass ?? fareClass}
          isDateRowSelected={isSelected}
          checkIn={checkIn}
          checkOut={checkOut}
          nights={nights}
          rooms={rooms}
          priceMode={priceMode}
          luxPlusEnabled={luxPlusEnabled}
          oneWayBundle={oneWayBundle}
          tripOption={carouselTripOption}
          onPickFare={handleCarouselPickFare}
          hotelPriceByCabinAndDate={hotelPriceByCabinAndDate}
        />
      )}
    </DateCard>
  )
}

export default MaldivesCharterDateFareCalendarItemMobile
